/**
 * @file NotRecording Component
 * @description Handles recording initialization and displays appropriate messages based on recording status
 * @module context/session/components/Rules
 */

import React, { useEffect, useState, useRef } from 'react';
import { Flex, Text, IconWarningLine, IconUnmutedLine, IconVideoCameraLine } from '@smarterservices/smarter-ui';
import SinglePane from '../SinglePane';
import { usePaneViewer } from '../PaneViewer';
import { useAppStore } from '../../stores/app';
import { useLogger } from '@ui/shared/components/Logger';
import Loader from '../Common/Loader';

/**
 * NotRecording Component
 * 
 * Handles different recording statuses and takes appropriate actions:
 * - NO_EGRESS: Starts recording and polls for confirmation
 * - EGRESS_STARTING: Polls for recording to become active
 * - EGRESS_LIMIT_REACHED: Displays capacity error
 * - Other statuses: Displays error message
 */
function NotRecording() {
  const { setHideTitle } = usePaneViewer();
  const { startRecording, getRecordingInfo } = useAppStore();
  const logger = useLogger();

  // Track current status: checking, starting, polling, capacity, error, success
  const [status, setStatus] = useState('checking');
  // User-facing message to display
  const [message, setMessage] = useState('Checking recording status...');
  // Counter for polling attempts (max 24 attempts over 2 minutes)
  const [pollingAttempts, setPollingAttempts] = useState(0);

  // Refs to store interval and timeout IDs for cleanup
  const pollIntervalRef = useRef(null);
  const reloadTimeoutRef = useRef(null);

  // Hide the pane viewer title when this component is mounted
  useEffect(() => {
    setHideTitle(true);

    // Restore title visibility when component unmounts
    return () => {
      setHideTitle(false);
    };
  }, [setHideTitle]);

  // Cleanup intervals and timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear polling interval if it's running
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      // Clear reload timeout if it's pending
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };
  }, []);

  // Check recording status on component mount
  useEffect(() => {
    handleRecordingStatus();
  }, []);

  /**
   * Checks the current recording status and takes appropriate action
   * 
   * Status handling:
   * - NO_EGRESS: No recording exists, need to start one
   * - EGRESS_STARTING: Recording is being initialized, poll for completion
   * - EGRESS_LIMIT_REACHED: Server capacity reached, show error
   * - Other: Unexpected status, show error and log
   */
  const handleRecordingStatus = async () => {
    try {
      // Force reload recording info from server
      const recordingInfo = await getRecordingInfo(true);
      const recordingStatus = recordingInfo?.status;

      // Case 1: No recording exists - start one and poll
      if (recordingStatus === 'NO_EGRESS') {
        setStatus('starting');
        setMessage('Starting recording...');
        await startRecordingAndPoll();
      }
      // Case 2: Recording is starting - just poll for completion
      else if (recordingStatus === 'EGRESS_STARTING') {
        setStatus('polling');
        setMessage('Recording is starting...');
        await pollRecordingStatus();
      }
      // Case 3: Server capacity reached - show error
      else if (recordingStatus === 'EGRESS_LIMIT_REACHED') {
        setStatus('capacity');
        setMessage('Recording capacity has been reached. Please try again later.');
        logger.logError('SessionUI', 'Recording capacity limit reached', { recordingInfo });
      }
      // Case 4: Unexpected status - show error
      else {
        setStatus('error');
        setMessage('Unable to start recording. Please close this session and try again.');
        logger.logError('SessionUI', `Unexpected recording status: ${recordingStatus}`, { recordingInfo });
      }
    } catch (error) {
      setStatus('error');
      setMessage('Recording Not. Please close this session and try again.');
      logger.logError('SessionUI', 'Error checking recording status', error);
    }
  };

  /**
   * Starts the recording with retry logic, then polls for confirmation
   * 
   * Flow:
   * 1. Call startRecording from app store (includes exponential backoff retry)
   * 2. If successful, begin polling to confirm recording is active
   * 3. If failed after all retries, show error message
   */
  const startRecordingAndPoll = async () => {
    try {
      // Start recording with up to 5 retry attempts
      const result = await startRecording({
        maxRetries: 5,
        onRetry: ({ attempt, maxRetries, backoffDelay }) => {
          // Log each retry attempt for debugging
          logger.logError('SessionUI', `Recording start failed, retrying in ${backoffDelay}ms (attempt ${attempt}/${maxRetries})...`);
        }
      });

      if (result.success) {
        // Recording start initiated, now poll to confirm it's active
        setStatus('polling');
        setMessage('Recording started, waiting for confirmation...');
        await pollRecordingStatus();
      } else {
        // All retries exhausted, show error
        setStatus('error');
        setMessage('Failed to start recording. Please close this session and try again.');
        logger.logError('SessionUI', 'Failed to start recording after retries', { result });
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error starting recording. Please close this session and try again.');
      logger.logError('SessionUI', 'Error in startRecordingAndPoll', error);
    }
  };

  /**
   * Polls the recording status every 5 seconds for up to 2 minutes
   * 
   * Polling configuration:
   * - Interval: 5 seconds between checks
   * - Duration: 2 minutes maximum (24 total attempts)
   * - Success: When recordingInfo.active === true
   * - Timeout: After 24 attempts without success
   * 
   * On success: Reloads the page to show the Rules component
   * On timeout: Shows error message
   */
  const pollRecordingStatus = async () => {
    const maxPollingTime = 2 * 60 * 1000; // 2 minutes total
    const pollInterval = 5000; // Check every 5 seconds
    const maxAttempts = maxPollingTime / pollInterval; // 24 total attempts
    let attempts = 0;

    // Store interval ID in ref for cleanup
    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      setPollingAttempts(attempts); // Update UI with current attempt number

      try {
        // Check if recording is now active
        const recordingInfo = await getRecordingInfo(true);

        // Success case: Recording is active
        if (recordingInfo?.active === true) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('success');
          setMessage('Recording is now active. Reloading...');

          // Reload page after 1 second to show Rules component
          // Store timeout ID in ref for cleanup
          reloadTimeoutRef.current = setTimeout(() => {
            window.location.reload();
          }, 1000);
          return;
        }

        // Timeout case: Max attempts reached without success
        if (attempts >= maxAttempts) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('error');
          setMessage('Recording did not start within the expected time. Please close this session and try again.');
          logger.logError('SessionUI', 'Recording polling timeout', { attempts, recordingInfo });
        }
      } catch (error) {
        // Log polling errors but continue trying
        logger.logError('SessionUI', 'Error polling recording status', error);

        // Only show error if we've exhausted all attempts
        if (attempts >= maxAttempts) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('error');
          setMessage('Error checking recording status. Please close this session and try again.');
        }
      }
    }, pollInterval);
  };

  /**
   * Renders appropriate icon based on current status
   * 
   * Icons:
   * - error: Alert circle (red/danger)
   * - capacity: Info circle (warning)
   * - success: Check circle (success/green)
   */
  const renderIcon = () => {
    if (status === 'error') {
      return <IconWarningLine size="large" color="danger" />;
    }
    if (status === 'capacity') {
      return <IconUnmutedLine size="large" color="warning" />;
    }
    if (status === 'success') {
      return <IconVideoCameraLine size="large" color="success" />;
    }
    return null;
  };

  /**
   * Renders appropriate UI based on current status
   * 
   * Loading states (checking, starting, polling):
   * - Shows Loader component with status message
   * - During polling, shows progress counter (X of 24)
   * 
   * Error/capacity/success states:
   * - Shows icon with message text
   */
  const renderContent = () => {
    // Show loader for active/in-progress states
    if (status === 'checking' || status === 'starting' || status === 'polling') {
      return (
        <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%" gap="medium">
          <Loader text={message} />
          {/* Show polling progress during active polling */}
          {status === 'polling' && pollingAttempts > 0 && (
            <Text size="small" color="secondary">
              Checking recording status... ({pollingAttempts} of 24)
            </Text>
          )}
        </Flex>
      );
    }

    // Show icon and message for error/capacity/success states
    const icon = renderIcon();
    return (
      <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%" gap="medium">
        {icon}
        <Text size="large" textAlign="center">{message}</Text>
      </Flex>
    );
  };

  return (
    <SinglePane>
      {renderContent()}
    </SinglePane>
  );
}

export default NotRecording;
